import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";

import PageTransition from "./components/PageTransition";
import Header from "./components/Header";
import DeepLinkRedirect from "./components/DeepLinkRedirect";

// Lazy load non-critical pages to reduce initial bundle size
const About = lazy(() => import("./pages/About"));
const Activities = lazy(() => import("./pages/Activities"));
const Benefits = lazy(() => import("./pages/Benefits"));
const Insights = lazy(() => import("./pages/Insights"));
const Team = lazy(() => import("./pages/Team"));
const Contact = lazy(() => import("./pages/Contact"));
const Apply = lazy(() => import("./pages/Apply"));
const PortfolioComposition = lazy(() => import("./pages/PortfolioComposition"));
const Brand = lazy(() => import("./pages/Brand"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Header />
        <PageTransition>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/activities" element={<Activities />} />
              <Route path="/benefits" element={<Benefits />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/team" element={<Team />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/portfolio" element={<PortfolioComposition />} />
              <Route path="/brand" element={<Brand />} />
              {/* Pretty deep-link paths — redirect to the canonical ?query= form
                  so the page's existing deep-link logic opens the right modal. */}
              <Route
                path="/insights/article/:slug"
                element={<DeepLinkRedirect to="/insights?article=:slug" fallback="/insights" />}
              />
              <Route
                path="/insights/report/:slug"
                element={<DeepLinkRedirect to="/insights?report=:slug" fallback="/insights" />}
              />
              <Route
                path="/activities/event/:slug"
                element={
                  <DeepLinkRedirect
                    to="/activities?tab=archive#event-:slug"
                    fallback="/activities"
                  />
                }
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </PageTransition>
        
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
